import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Avatar from '@/components/Avatar';
import PersonIdentity from '@/components/PersonIdentity';
import {
  ChevronLeft as ChevronL,
  ChevronRight as ChevronR,
  Close as CloseIcon,
} from '@/components/icons';
import { useOrg, ALL_ORGS } from '@/context/OrgContext';
import { useData } from '@/lib/useData';
import { formatDate, isToday, safeId } from '@/lib/format';
import type { CalendarDay } from '@/lib/types';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
type Tab = 'leaders' | 'commits' | 'shipped' | 'repos';

export default function Calendar() {
  const data = useData();
  const { org, label } = useOrg();
  const cal = data.calendar[org];

  const today = new Date();
  const [view, setView] = useState<{ year: number; month: number }>({
    year: today.getFullYear(),
    month: today.getMonth(),
  });

  const byDate = useMemo(() => new Map(cal?.days.map((d) => [d.date, d]) ?? []), [cal]);
  const [selectedIso, setSelectedIso] = useState<string | null>(null);
  const selected = selectedIso ? byDate.get(selectedIso) ?? null : null;

  const startBound = cal ? new Date(cal.start) : null;
  const endBound   = cal ? new Date(cal.end)   : null;
  const canPrev = true;
  const canNext = true;

  function nav(delta: number) {
    setView((v) => {
      const next = new Date(v.year, v.month + delta, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });
  }
  function jumpToday() {
    setView({ year: today.getFullYear(), month: today.getMonth() });
  }

  const days = useMemo(() => buildMonth(view.year, view.month, byDate), [view, byDate]);

  if (!cal) return null;

  return (
    <div className="max-w-[1100px] mx-auto flex flex-col h-[calc(100vh-88px)]">
      {/* Header bar */}
      <div className="flex items-center justify-between mb-3 sm:mb-5 shrink-0 gap-2">
        <div className="flex items-center gap-1">
          <MonthPicker
            year={view.year}
            month={view.month}
            onChange={(y, m) => setView({ year: y, month: m })}
            minYear={(startBound?.getFullYear() ?? view.year) - 5}
            maxYear={(endBound?.getFullYear()   ?? view.year) + 1}
          />
          <button type="button" onClick={() => nav(-1)} disabled={!canPrev}
            className="icon-btn disabled:opacity-30 disabled:pointer-events-none" aria-label="Previous month">
            <ChevronL />
          </button>
          <button type="button" onClick={() => nav(1)} disabled={!canNext}
            className="icon-btn disabled:opacity-30 disabled:pointer-events-none" aria-label="Next month">
            <ChevronR />
          </button>
        </div>
        <div className="flex items-center gap-2">
          {org !== ALL_ORGS && (
            <span className="text-[11px] text-fg-subtle">{label(org)}</span>
          )}
          <button type="button" onClick={jumpToday} className="pill">Today</button>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="flex-1 min-h-0">
        <MonthGrid
          days={days}
          selectedDate={selectedIso}
          onSelect={(iso) => setSelectedIso(iso)}
        />
      </div>

      {/* Drawer overlay */}
      <AnimatePresence>
        {selected && (
          <DayDrawer
            key={selected.date}
            day={selected}
            onClose={() => setSelectedIso(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// --- month building -------------------------------------------------------

interface MonthDay { iso: string; day: number; inMonth: boolean; data: CalendarDay | null }

function buildMonth(year: number, month: number, byDate: Map<string, CalendarDay>): MonthDay[] {
  const first = new Date(year, month, 1);
  const last  = new Date(year, month + 1, 0);
  const firstWd = (first.getDay() + 6) % 7; // Mon = 0
  const days: MonthDay[] = [];
  for (let i = firstWd - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    const iso = d.toISOString().slice(0, 10);
    days.push({ iso, day: d.getDate(), inMonth: false, data: byDate.get(iso) ?? null });
  }
  for (let d = 1; d <= last.getDate(); d++) {
    const dt = new Date(year, month, d);
    const iso = dt.toISOString().slice(0, 10);
    days.push({ iso, day: d, inMonth: true, data: byDate.get(iso) ?? null });
  }
  const trail = (7 - (days.length % 7)) % 7;
  for (let i = 1; i <= trail; i++) {
    const dt = new Date(year, month + 1, i);
    const iso = dt.toISOString().slice(0, 10);
    days.push({ iso, day: i, inMonth: false, data: byDate.get(iso) ?? null });
  }
  return days;
}

// --- month grid ----------------------------------------------------------

function MonthGrid({
  days, selectedDate, onSelect,
}: { days: MonthDay[]; selectedDate: string | null; onSelect: (iso: string) => void }) {
  const weekdays = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const numRows = Math.max(1, Math.ceil(days.length / 7));
  return (
    <div className="card overflow-hidden flex flex-col h-full">
      <div className="grid grid-cols-7 text-[10px] sm:text-[11px] font-semibold text-fg-subtle border-b shrink-0">
        {weekdays.map((w) => (
          <div key={w} className="px-1.5 sm:px-3 py-2 uppercase tracking-wider">
            <span className="hidden sm:inline">{w}</span>
            <span className="sm:hidden">{w[0]}</span>
          </div>
        ))}
      </div>
      <div
        className="grid grid-cols-7 flex-1 min-h-0"
        style={{ gridTemplateRows: `repeat(${numRows}, minmax(0, 1fr))` }}
      >
        {days.map((d, i) => {
          const activity = d.data ? d.data.total_commits + d.data.total_prs + d.data.total_reviews : 0;
          const sel = d.iso === selectedDate;
          const today = isToday(d.iso);
          return (
            <button
              key={`${d.iso}-${i}`}
              onClick={() => d.inMonth && onSelect(d.iso)}
              disabled={!d.inMonth}
              className={[
                'relative border-r border-b p-1 sm:p-2 text-left transition-colors overflow-hidden min-h-0',
                d.inMonth ? 'hover:bg-surface-hover' : 'opacity-30',
                sel ? 'bg-surface-hover ring-1 ring-inset ring-fg/20' : '',
                i % 7 === 6 ? 'border-r-0' : '',
                i >= days.length - 7 ? 'border-b-0' : '',
              ].join(' ')}
              aria-label={`${formatDate(d.iso)}: ${activity} events`}
            >
              <div className="flex items-start justify-between">
                <span className={[
                  'inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full text-[10px] sm:text-[11px] font-semibold num',
                  today ? 'bg-fivec text-white'
                    : sel ? 'text-fg'
                    : d.inMonth ? 'text-fg' : 'text-fg-muted',
                ].join(' ')}>
                  {d.day}
                </span>
              </div>
              {d.data && activity > 0 && (
                <>
                  {/* Mobile: compact dot-indicator row (pills don't fit on 50px cells). */}
                  <div className="mt-1 flex items-center gap-1 sm:hidden">
                    {d.data.total_commits > 0 && <span className="w-1.5 h-1.5 rounded-full bg-accent" title={`${d.data.total_commits} commits`} />}
                    {d.data.total_prs > 0     && <span className="w-1.5 h-1.5 rounded-full bg-fivec"  title={`${d.data.total_prs} shipped`} />}
                    {d.data.total_reviews > 0 && <span className="w-1.5 h-1.5 rounded-full bg-fg-subtle" title={`${d.data.total_reviews} reviews`} />}
                  </div>
                  {/* Desktop: full event pills. */}
                  <div className="hidden sm:block mt-1.5 space-y-0.5 overflow-hidden">
                    {d.data.total_commits > 0 && (
                      <EventPill color="commits">{d.data.total_commits} commits</EventPill>
                    )}
                    {d.data.total_prs > 0 && (
                      <EventPill color="prs">{d.data.total_prs} shipped</EventPill>
                    )}
                    {d.data.total_reviews > 0 && (
                      <EventPill color="reviews">{d.data.total_reviews} reviews</EventPill>
                    )}
                  </div>
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EventPill({ color, children }: { color: 'commits' | 'prs' | 'reviews'; children: React.ReactNode }) {
  const cls = color === 'commits'
    ? 'bg-accent/10 text-accent-600 dark:bg-accent/12 dark:text-accent-300'
    : color === 'prs'
      ? 'bg-fivec/8 text-fivec-600 dark:bg-fivec/10 dark:text-fivec-400'
      : 'bg-fg/6 text-fg-muted';
  return (
    <div className={[
      'truncate rounded-[4px] px-1.5 py-[1px] text-[10px] font-medium',
      cls,
    ].join(' ')}>
      {children}
    </div>
  );
}

// --- month/year picker ----------------------------------------------------

function MonthPicker({
  year, month, onChange, minYear, maxYear,
}: {
  year: number; month: number;
  onChange: (y: number, m: number) => void;
  minYear: number; maxYear: number;
}) {
  const [open, setOpen] = useState(false);
  const [pickYear, setPickYear] = useState(year);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => { setPickYear(year); }, [year, open]);
  useEffect(() => {
    if (!open) return;
    const click = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', click);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', click);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 h-8 px-2 rounded-md text-[15px] font-semibold text-fg hover:bg-surface-hover transition-colors"
      >
        {MONTHS[month]} {year}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11" className={`text-fg-subtle transition-transform ${open ? 'rotate-180' : ''}`}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1.5 w-[260px] rounded-md bg-bg-elev border border-fg/10 shadow-lg p-2 z-50">
          <div className="flex items-center justify-between px-1 mb-2">
            <button type="button"
              onClick={() => setPickYear((y) => Math.max(minYear, y - 1))}
              disabled={pickYear <= minYear}
              className="icon-btn disabled:opacity-30 disabled:pointer-events-none"
              aria-label="Previous year"><ChevronL /></button>
            <div className="text-[13px] font-semibold num">{pickYear}</div>
            <button type="button"
              onClick={() => setPickYear((y) => Math.min(maxYear, y + 1))}
              disabled={pickYear >= maxYear}
              className="icon-btn disabled:opacity-30 disabled:pointer-events-none"
              aria-label="Next year"><ChevronR /></button>
          </div>
          <div className="grid grid-cols-3 gap-1">
            {MONTHS.map((m, i) => {
              const sel = year === pickYear && month === i;
              return (
                <button
                  key={m}
                  onClick={() => { onChange(pickYear, i); setOpen(false); }}
                  className={[
                    'h-8 text-[12px] rounded-[5px] font-medium transition-colors',
                    sel ? 'bg-surface-hover text-fg' : 'text-fg-muted hover:bg-surface-hover hover:text-fg',
                  ].join(' ')}
                >
                  {m.slice(0, 3)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// --- day drawer -----------------------------------------------------------

function DayDrawer({ day, onClose }: { day: CalendarDay; onClose: () => void }) {
  const [tab, setTab] = useState<Tab>('leaders');

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', esc);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const empty = day.total_commits + day.total_prs + day.total_reviews === 0;

  return (
    <>
      <motion.div
        className="fixed inset-0 bg-bg-deep/40 backdrop-blur-[2px] z-40"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.aside
        className="fixed top-0 right-0 bottom-0 w-full sm:w-[460px] bg-bg-elev border-l shadow-lg z-50 flex flex-col"
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', stiffness: 360, damping: 36 }}
        role="dialog" aria-label={`Activity on ${day.date}`}
      >
        <header className="p-5 border-b">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-fg-subtle">
                {isToday(day.date) ? 'Today' : 'Selected day'}
              </div>
              <h3 className="mt-1 text-[18px] font-semibold text-fg">{formatDate(day.date)}</h3>
            </div>
            <button onClick={onClose} className="icon-btn" aria-label="Close">
              <CloseIcon />
            </button>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-4 text-[13px]">
            <Stat label="Commits" value={day.total_commits} />
            <Stat label="PRs" value={day.total_prs} />
            <Stat label="Reviews" value={day.total_reviews} />
          </div>
        </header>

        {!empty && (
          <nav className="px-5 flex items-center gap-5" aria-label="Day view tabs">
            <TabLink active={tab === 'leaders'} onClick={() => setTab('leaders')}>Leaderboard</TabLink>
            <TabLink active={tab === 'commits'} onClick={() => setTab('commits')}>Commits</TabLink>
            <TabLink active={tab === 'shipped'} onClick={() => setTab('shipped')}>Shipped</TabLink>
            <TabLink active={tab === 'repos'}   onClick={() => setTab('repos')}>Repos</TabLink>
          </nav>
        )}

        <div className="flex-1 overflow-y-auto overscroll-contain p-5">
          {empty ? (
            <p className="text-[13px] text-fg-subtle">Nothing was shipped this day.</p>
          ) : tab === 'leaders' ? (
            <LeadersView day={day} />
          ) : tab === 'commits' ? (
            <CommitsView day={day} />
          ) : tab === 'shipped' ? (
            <ShippedView day={day} />
          ) : (
            <ReposView day={day} />
          )}
        </div>
      </motion.aside>
    </>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-[18px] font-semibold num text-fg">{value}</div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mt-0.5">{label}</div>
    </div>
  );
}

function TabLink({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'relative py-2.5 text-[12px] font-medium transition-colors',
        active ? 'text-fg' : 'text-fg-muted hover:text-fg',
      ].join(' ')}
    >
      {children}
      {active && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-accent-500 rounded-full" />}
    </button>
  );
}


function LeadersView({ day }: { day: CalendarDay }) {
  const items = [
    { key: 'commits',  label: 'Top pusher',   unit: 'commits', leader: day.leaders.commits },
    { key: 'prs',      label: 'Top shipper',  unit: 'PRs',     leader: day.leaders.prs },
    { key: 'reviews',  label: 'Top reviewer', unit: 'reviews', leader: day.leaders.reviews },
  ];
  const any = items.some((i) => i.leader);

  return (
    <div className="space-y-5">
      {any && (
        <section className="space-y-2.5">
          <SectionHeader>Top of the day</SectionHeader>
          {items.map((i) => i.leader && (
            <Link key={i.key} to={`/profiles/${safeId(i.leader!.canonical_id)}`}
              className="flex items-center gap-2.5 p-2 -mx-2 rounded-md hover:bg-surface-hover transition-colors">
              <Avatar name={i.leader!.display_name} login={i.leader!.primary_login} size={28} />
              <div className="min-w-0 flex-1">
                <PersonIdentity name={i.leader!.display_name} login={i.leader!.primary_login} size="sm" />
                <div className="text-[10px] uppercase tracking-wider text-fg-subtle mt-0.5">{i.label}</div>
              </div>
              <div className="text-right">
                <div className="text-[13px] font-semibold num">{i.leader!.value}</div>
                <div className="text-[9px] uppercase tracking-wider text-fg-subtle">{i.unit}</div>
              </div>
            </Link>
          ))}
        </section>
      )}

      <section className="space-y-2">
        <SectionHeader>Everyone who shipped</SectionHeader>
        <div className="space-y-0.5">
          {day.contributors.map((c) => (
            <Link key={c.canonical_id} to={`/profiles/${safeId(c.canonical_id)}`}
              className="flex items-center gap-2.5 px-1.5 py-1.5 rounded-md hover:bg-surface-hover transition-colors">
              <Avatar name={c.display_name} login={c.primary_login} size={24} />
              <PersonIdentity name={c.display_name} login={c.primary_login} size="sm" />
              <div className="ml-auto flex items-center gap-1 text-[10px] num font-medium">
                {c.commits > 0 && (
                  <span className="chip" title={`${c.commits} commits`}>
                    <span className="chip-dot bg-accent" />
                    <span className="num">{c.commits}</span>
                    <span className="text-fg-subtle font-normal">commits</span>
                  </span>
                )}
                {c.prs > 0 && (
                  <span className="chip" title={`${c.prs} PRs merged`}>
                    <span className="chip-dot bg-fivec" />
                    <span className="num">{c.prs}</span>
                    <span className="text-fg-subtle font-normal">PRs</span>
                  </span>
                )}
                {c.reviews > 0 && (
                  <span className="chip" title={`${c.reviews} reviews`}>
                    <span className="chip-dot bg-fg-subtle" />
                    <span className="num">{c.reviews}</span>
                    <span className="text-fg-subtle font-normal">reviews</span>
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function CommitsView({ day }: { day: CalendarDay }) {
  const rows = day.contributors
    .filter((c) => c.commits > 0)
    .slice()
    .sort((a, b) => b.commits - a.commits);
  if (rows.length === 0) {
    return <p className="text-[13px] text-fg-subtle">No commits this day.</p>;
  }
  return (
    <div className="space-y-2">
      <SectionHeader>
        {day.total_commits} commit{day.total_commits === 1 ? '' : 's'} | {rows.length} {rows.length === 1 ? 'author' : 'authors'}
      </SectionHeader>
      <div className="space-y-0.5">
        {rows.map((c) => (
          <Link
            key={c.canonical_id}
            to={`/profiles/${safeId(c.canonical_id)}`}
            className="flex items-center gap-2.5 px-1.5 py-1.5 rounded-md hover:bg-surface-hover transition-colors"
          >
            <Avatar name={c.display_name} login={c.primary_login} size={24} />
            <PersonIdentity name={c.display_name} login={c.primary_login} size="sm" />
            <span className="ml-auto text-[13px] font-semibold num text-fg tabular-nums">
              {c.commits}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function ShippedView({ day }: { day: CalendarDay }) {
  if (day.pr_events.length === 0) {
    return <p className="text-[13px] text-fg-subtle">No PRs merged this day.</p>;
  }
  return (
    <div className="space-y-2">
      <SectionHeader>{day.pr_events.length} PR{day.pr_events.length === 1 ? '' : 's'} merged</SectionHeader>
      {day.pr_events.map((e) => (
        <a
          key={`${e.org}-${e.repo}-${e.number}`}
          href={`https://github.com/${e.org}/${e.repo}/pull/${e.number}`}
          target="_blank" rel="noreferrer"
          className="block p-2.5 -mx-2.5 rounded-md hover:bg-surface-hover transition-colors"
        >
          <div className="flex items-center gap-2 text-[11px] text-fg-subtle">
            <span className="mono">{e.org}/{e.repo}</span>
            <span>|</span>
            <span className="mono">#{e.number}</span>
            {e.is_bhai_assisted === 1 && (
              <span className="chip"><span className="chip-dot bg-fivec" />BHAI</span>
            )}
          </div>
          <p className="mt-1 text-[13px] text-fg truncate">{e.title}</p>
          <div className="mt-1.5 flex items-center gap-2 text-[11px] text-fg-muted">
            <Avatar name={e.display_name} login={e.primary_login} size={18} />
            <span className="truncate">{e.display_name}</span>
          </div>
        </a>
      ))}
    </div>
  );
}

function ReposView({ day }: { day: CalendarDay }) {
  if (day.repo_activity.length === 0) {
    return <p className="text-[13px] text-fg-subtle">No repository activity this day.</p>;
  }
  const max = Math.max(1, ...day.repo_activity.map((r) => r.commits + r.prs));
  return (
    <div className="space-y-2">
      <SectionHeader>{day.repo_activity.length} repo{day.repo_activity.length === 1 ? '' : 's'} touched</SectionHeader>
      {day.repo_activity.map((r) => {
        const total = r.commits + r.prs;
        const ratio = total / max;
        return (
          <div key={r.full_name} className="p-2.5 -mx-2.5 rounded-md hover:bg-surface-hover transition-colors">
            <div className="flex items-center gap-2">
              <a
                href={`https://github.com/${r.org}/${r.repo}`}
                target="_blank" rel="noreferrer"
                className="mono text-[12px] font-semibold text-fg truncate flex-1 hover:underline"
              >
                {r.org}/{r.repo}
              </a>
              <div className="flex items-center gap-1 text-[10px] num font-medium">
                {r.commits > 0 && <span className="chip"><span className="chip-dot bg-accent" />{r.commits}c</span>}
                {r.prs > 0 && <span className="chip"><span className="chip-dot bg-fivec" />{r.prs}p</span>}
              </div>
            </div>
            <div className="mt-1.5 h-1 rounded-full bg-fg/5 overflow-hidden">
              <div className="h-full bg-accent/70" style={{ width: `${Math.round(ratio * 100)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] uppercase tracking-wider font-semibold text-fg-subtle">
      {children}
    </div>
  );
}

